import type { ReactNode } from 'react'
import { Box, Card, CardContent, IconButton, Typography } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

interface NumberedSectionCardProps {
  index: number
  title: string
  hint?: ReactNode
  locked?: boolean
  children: ReactNode
  // When collapsible, a toggle appears in the header; collapsed shows `summary` instead of children.
  collapsible?: boolean
  collapsed?: boolean
  onToggleCollapsed?: () => void
  summary?: ReactNode
  testId?: string
}

/**
 * A numbered step card, optionally collapsible.
 *
 * Named for its numbering rather than for a feature, because `Generic/SectionCard` already owns
 * the plain-card name: that one is a content card with an accent bar and a footer, and the two are
 * not interchangeable at an import site.
 */
const NumberedSectionCard = ({
  index,
  title,
  hint,
  locked = false,
  children,
  collapsible = false,
  collapsed = false,
  onToggleCollapsed,
  summary,
  testId,
}: NumberedSectionCardProps) => {
  const showToggle = collapsible && !locked
  const isCollapsed = showToggle && collapsed
  return (
    <Card
      variant="outlined"
      sx={{ opacity: locked ? 0.55 : 1, pointerEvents: locked ? 'none' : 'auto' }}
      aria-disabled={locked}
      data-testid={testId}
    >
      <CardContent>
        <Box
          onClick={showToggle ? onToggleCollapsed : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            cursor: showToggle ? 'pointer' : 'default',
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              bgcolor: 'primary.light',
              color: 'primary.main',
              display: 'grid',
              placeItems: 'center',
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {index}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
            {title}
          </Typography>
          {showToggle && (
            <IconButton
              size="small"
              onClick={event => {
                // The header row also toggles; don't double-fire when the icon itself is clicked.
                event.stopPropagation()
                onToggleCollapsed?.()
              }}
              aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
              data-testid={testId ? `${testId}-toggle` : undefined}
            >
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          )}
        </Box>
        {isCollapsed ? (
          summary && <Box sx={{ mt: 1 }}>{summary}</Box>
        ) : (
          <Box sx={{ mt: hint ? 0.5 : 2 }}>
            {hint && (
              <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>{hint}</Typography>
            )}
            {children}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default NumberedSectionCard
